import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Globe, BookOpen, Volume2, Languages, LogOut, Award, Key } from 'lucide-react';
import { languages } from '../mockData';
import { toast } from '../hooks/use-toast';
import { progressAPI, userAPI } from '../services/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState({ name: 'Learner' });
  const [selectedLanguage, setSelectedLanguage] = useState('spanish');
  const [progress, setProgress] = useState({
    nouns_completed: 0,
    verbs_completed: 0,
    questions_completed: 0,
    completion_percentage: 0,
    streak_days: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const isAuth = localStorage.getItem('isAuthenticated');
    if (!isAuth) {
      navigate('/login');
      return;
    }
    
    const lang = localStorage.getItem('selectedLanguage') || 'spanish';
    setSelectedLanguage(lang);
    
    fetchUserData(lang);
  }, [navigate]);

  const fetchUserData = async (lang) => {
    try {
      const [userProfile, userProgress] = await Promise.all([
        userAPI.getProfile(),
        progressAPI.getProgress(lang)
      ]);
      
      setUser(userProfile);
      setProgress(userProgress);
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load user data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    toast({
      title: 'Logged out',
      description: 'See you next time!',
    });
    navigate('/');
  };

  const selectedLang = languages.find(l => l.id === selectedLanguage);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-sky-50">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Globe className="h-8 w-8 text-amber-600" />
              <span className="text-lg sm:text-xl font-bold text-slate-900">NomadLinguist</span>
            </div>
            <div className="flex items-center space-x-1 sm:space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/change-password')} data-testid="change-password-btn" className="hidden sm:flex">
                <Key className="h-4 w-4 mr-2" />
                Change Password
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">
            Welcome back, {user.name}!
          </h1>
          <p className="text-xl text-slate-600">
            Continue your language learning journey
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="border-2">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Current Language</CardTitle>
              <Languages className="h-4 w-4 text-amber-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{selectedLang?.name}</div>
              <p className="text-xs text-slate-600 mt-1">{selectedLang?.nativeName}</p>
              <Button
                variant="link"
                className="px-0 text-amber-600 mt-2"
                onClick={() => navigate('/language-selection')}
              >
                Change Language
              </Button>
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Progress</CardTitle>
              <BookOpen className="h-4 w-4 text-sky-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{progress.completion_percentage}%</div>
              <Progress value={progress.completion_percentage} className="mt-2" />
              <p className="text-xs text-slate-600 mt-2">
                {progress.total_completed}/50 items completed
              </p>
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Learning Streak</CardTitle>
              <Award className="h-4 w-4 text-emerald-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{progress.streak_days} Days</div>
              <p className="text-xs text-slate-600 mt-1">Keep going!</p>
            </CardContent>
          </Card>
        </div>

        {/* Learning Sections */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Quick Learning</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card 
              className="border-2 hover:border-amber-400 hover:shadow-lg transition-all cursor-pointer"
              onClick={() => navigate('/learn/nouns')}
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-amber-600" />
                  </div>
                  Common Nouns
                </CardTitle>
                <CardDescription>
                  Learn essential words for travel
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Progress value={(progress.nouns_completed / 15) * 100} className="mb-2" />
                <p className="text-sm text-slate-600">{progress.nouns_completed}/15 completed</p>
              </CardContent>
            </Card>

            <Card 
              className="border-2 hover:border-amber-400 hover:shadow-lg transition-all cursor-pointer"
              onClick={() => navigate('/learn/verbs')}
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                    <Volume2 className="h-5 w-5 text-sky-600" />
                  </div>
                  Common Verbs
                </CardTitle>
                <CardDescription>
                  Master key action words
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Progress value={(progress.verbs_completed / 15) * 100} className="mb-2" />
                <p className="text-sm text-slate-600">{progress.verbs_completed}/15 completed</p>
              </CardContent>
            </Card>

            <Card 
              className="border-2 hover:border-amber-400 hover:shadow-lg transition-all cursor-pointer"
              onClick={() => navigate('/learn/questions')}
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Languages className="h-5 w-5 text-emerald-600" />
                  </div>
                  Common Questions
                </CardTitle>
                <CardDescription>
                  Real travel conversations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Progress value={(progress.questions_completed / 20) * 100} className="mb-2" />
                <p className="text-sm text-slate-600">{progress.questions_completed}/20 completed</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* 15-Day Program Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Comprehensive Learning</h2>
          <Card 
            className="border-2 border-amber-400 bg-gradient-to-r from-amber-50 to-sky-50 hover:shadow-xl transition-all cursor-pointer"
            onClick={() => navigate('/program')}
          >
            <CardContent className="p-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <div className="w-16 h-16 bg-amber-600 rounded-xl flex items-center justify-center">
                    <Award className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">
                      15-Day Language Program
                    </h3>
                    <p className="text-slate-700 mb-1">
                      Master {selectedLang?.name} from beginner to intermediate in just 15 days
                    </p>
                    <p className="text-sm text-slate-600">
                      ✓ Structured lessons  ✓ Daily tests  ✓ AI-powered  ✓ Final certification
                    </p>
                  </div>
                </div>
                <Button className="bg-amber-600 hover:bg-amber-700">
                  Start Program
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Translator */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle>Quick Translator</CardTitle>
            <CardDescription>Translate English to {selectedLang?.name} on the go</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => navigate('/translator')}
            >
              Open Translator
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Mobile Bottom Navigation - positioned above AdMob banner */}
      <div className="fixed left-0 right-0 bg-white border-t sm:hidden z-50" style={{ bottom: '60px', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="flex justify-around items-center h-16">
          <Button variant="ghost" size="sm" className="flex-col h-14 px-2" onClick={() => navigate('/change-password')}>
            <Key className="h-5 w-5 text-slate-600" />
            <span className="text-xs mt-1">Password</span>
          </Button>
          <Button variant="ghost" size="sm" className="flex-col h-14 px-2" onClick={() => navigate('/translator')}>
            <Languages className="h-5 w-5 text-slate-600" />
            <span className="text-xs mt-1">Translate</span>
          </Button>
          <Button variant="ghost" size="sm" className="flex-col h-14 px-2" onClick={handleLogout}>
            <LogOut className="h-5 w-5 text-slate-600" />
            <span className="text-xs mt-1">Logout</span>
          </Button>
        </div>
      </div>

      {/* Spacer for mobile bottom nav + ad banner */}
      <div className="h-36 sm:hidden"></div>
    </div>
  );
};

export default Dashboard;